import axios from "axios";
import JSZip from "jszip";
import assign from "lodash/assign";
import merge from "lodash/merge";
import { getImagery } from "./imagery";
import { getInfo } from "./info";
import { hasValidPrescription } from "./prescription";
import { getReview } from "./review";
import {
  getFirstSuggestion,
  getFourthSuggestion,
  getSecondSuggestion,
  getThirdSuggestion,
} from "./suggestions";
import {
  CartUpsellingToolInput,
  CartUpsellingToolOutput,
  LensPackage,
  Package,
} from "./types";
import { sunRule } from "./utils";

const UPSELL_ORDERED_KEY_LIST: (keyof LensPackage)[] = [
  "antiReflective",
  "blueLight",
  "brand",
  "antiFog",
];

export function getChannelFromBrand(brand: string) {
  switch (brand) {
    case "nuance":
      return "NUA";
    case "arnette":
      return "AN";
    case "clearly":
      return "CL";
    case "costa":
      return "CM";
    case "davidclulow":
      return "DC";
    case "default":
      return "LC";
    case "framesdirect":
      return "FD";
    case "glasses":
      return "GL";
    case "oakley":
      return "OO";
    case "oliverpeoples":
      return "OP";
    case "opsm":
      return "OPSM";
    case "osi":
      return "OSI";
    case "persol":
      return "PO";
    case "rayban":
      return "RB";
    case "sgh":
      return "SGH";
    case "targetoptical":
      return "TO";
    default:
      return "LC";
  }
}
export const env = () => {
  const scripts = Array.from(document.getElementsByTagName("script"));
  const rxcScript = scripts.find((script) => {
    return script?.src?.includes("rxc.js");
  });
  const splittedScript = rxcScript?.src.split(/[//]/);
  const env = splittedScript?.[5];
  return env;
};
//funzioni per prendere content da backend ed unzip
export const extractJsonfromZip = async (file) => {
  const zip = new JSZip();
  const extractedFiles = await zip.loadAsync(file);
  const jsonResults = await Promise.all(
    Object.keys(extractedFiles.files).map(async (filename) => {
      const content = await zip.file(filename)?.async("string");
      if (content) return JSON.parse(content);
    }),
  );
  return jsonResults;
};

const setLensesDataPackages = (
  data: any,
  frameModel: string,
  frameColor: string,
) => {
  const moco = `${frameModel}__${frameColor}`;
  const lensesIds = data.frames[moco];
  let packages = Object.keys(data.packages)
    .filter((lensId) => lensesIds.includes(lensId))
    .reduce((acc: any, lensId: any) => {
      const lensPkgs = data.packages[lensId].map((pkg: any) => ({
        /*frame: {
          offerPrice: "",
          insPrice: null,
        },*/
        lensPackage: pkg,
      }));
      return [...acc, ...lensPkgs];
    }, []);
  //.filter((pkg: any) => isPublished(locale, pkg.lensPackage.published));

  /*packages.forEach((pkg: any) => {
    pkg.lensPackage.rxValues = getRxValues(locale, pkg.lensPackage.rxValues);
  });*/

  console.log(packages);
  return packages;
};

export const getLensesDataContent = (url) => {
  window?.quicklink?.prefetch(url);
  return axios
    .get(url, {
      responseType: "arraybuffer",
      decompress: true,
    })
    .then((res) => {
      console.log("getLensesDataContent res", res);
      return extractJsonfromZip(res.data);
    })
    .catch((e) => {
      console.log(e);
    });
};

export const getLensesDataPackages = (
  url: string,
  frameModel: string,
  frameColor: string,
) => {
  return axios
    .get(url, {
      responseType: "arraybuffer",
      decompress: true,
    })
    .then(async (res) => {
      console.log("getLensesDataPackages res", res);
      const dataPackages = await extractJsonfromZip(res.data);
      if (dataPackages.length > 0) {
        return setLensesDataPackages(dataPackages[0], frameModel, frameColor);
      }
      return [];
    })
    .catch((e) => {
      console.log(e);
    });
};

const noPricesInPackages = (packages) => {
  if (!packages) return null;
  const onlyLensPackages = packages.find((pkg) => pkg?.frame)
    ? packages.map((pkg) => pkg.lensPackage)
    : packages;
  return onlyLensPackages.every((pkg) => {
    const keys = Object.keys(pkg);
    return !keys.includes("offerPrice") && !keys.includes("listPrice");
  });
};

export const getLensesDataPrices = (url) => {
  window?.quicklink?.prefetch(url);
  return axios
    .get(url)
    .then((res) => {
      console.log("getLensesDataPrices res", res);
      return res.data;
    })
    .catch((e) => {
      console.log(e);
    });
};

const mergeLensesDataPrices = (packages, prices) => {
  return packages
    .filter(({ lensPackage }) => {
      return prices.find((p) => p.lensPackage.upc === lensPackage.upc);
    })
    .map(({ frame, lensPackage }) => {
      const pkg = prices.find((p) => p.lensPackage.upc === lensPackage.upc);
      return {
        frame: {
          ...frame,
          ...(pkg?.frame ? pkg.frame : {}),
        },
        lensPackage: {
          ...assign({}, pkg?.lensPackage, lensPackage),
          offerPrice: pkg?.lensPackage.offerPrice,
          listPrice: pkg?.lensPackage.listPrice,
        },
      };
    });
};

const mergeAkamaiPrices = (packages, prices) => {
  return packages
    .filter(({ lensPackage }) => {
      return prices.prices.find((p) => p.lensPackage.upc === lensPackage.upc);
    })
    .map(({ frame, lensPackage }) => {
      const pkg = prices.prices.find(
        (p) => p.lensPackage.upc === lensPackage.upc,
      );
      return {
        frame: {
          ...frame,
        },
        lensPackage: {
          ...assign({}, pkg?.lensPackage, lensPackage),
          services: merge(lensPackage.services, pkg?.lensPackage.services),
          offerPrice: pkg?.lensPackage.offerPrice,
          listPrice: pkg?.lensPackage.listPrice,
        },
      };
    });
};

const getRxValues = (locale, rxValues) => {
  if (!rxValues[locale]) {
    const defaultRxValues = rxValues["en_US"];
    if (defaultRxValues) {
      return defaultRxValues;
    } else {
      if (rxValues.hasOwnProperty("sph")) {
        return rxValues;
      } else {
        throw new Error("no rxValues for en_US");
      }
    }
  } else {
    return rxValues[locale];
  }
};

const prepareResponseMapper = (
  suggestion: Package,
  input: {
    _content: CartUpsellingToolInput["_content"];
    _locale: CartUpsellingToolInput["_locale"];
    _packages: CartUpsellingToolInput["_packages"];
    _currentPackage: CartUpsellingToolInput["_currentPackage"];
    _imagery: CartUpsellingToolInput["_imagery"];
    _config: CartUpsellingToolInput["_config"];
  },
  key: keyof LensPackage,
) => {
  const lensPackage = {
    ...suggestion.lensPackage,
    rxValues: getRxValues(input._locale, suggestion.lensPackage.rxValues),
  };
  return getReview(suggestion, input).then((reviewObject) => {
    let mergedFrame = input?._config?.frame;
    for (let key in suggestion?.frame) {
      if (
        suggestion?.frame?.[key] !== null &&
        suggestion?.frame?.[key] !== undefined
      ) {
        mergedFrame[key] = suggestion?.frame?.[key];
      }
    }
    return {
      frame: mergedFrame || input?._config?.frame || {},
      lensPackage: lensPackage,
      reviewObject: reviewObject,
      imagery: getImagery(suggestion, input),
      config: input?._config || {},
      info: getInfo(suggestion, input, key),
    };
  });
};

export async function getUpsellingLensPackages(
  input: CartUpsellingToolInput,
): Promise<CartUpsellingToolOutput> {
  if (!input._locale) input._locale = "en_US";

  if (!input._packages && input._config?.packages)
    input._packages = input._config?.packages;

  if (!input._content && input._config?.content)
    input._content = input._config?.content;

  //getSelected

  //if (!selected) throw new Error(`Not found the selected lenses`);

  //filter packages by prescription
  if (!input._locale) input._locale = "en_US";

  const country = input._locale?.split("_")?.[1] || "US";

  const category =
    input._config?.frame?.category ??
    ((input?._packages
      ?.filter(
        (pkg: any) => pkg.lensPackage.type.toLowerCase() !== "frame only",
      )
      ?.every((p) => sunRule(p.lensPackage)) &&
      "SUNGLASSES") ||
      "EYEGLASSES");
  if (input?._config?.brand) {
    const channel = getChannelFromBrand(input?._config?.brand);
    const baseURL =
      input._config?.baseURLs?.assetsCDN ||
      `https://assets.${input?._config?.brand}.com`;
    const URLS = {
      content: `${baseURL}/extra/image/rxcbe/${
        env() || "test"
      }/pof/${channel}/pof_${channel}_${category}_${input?._locale}.json.zip`,
      packages: `${baseURL}/extra/image/rxcbe/${
        env() || "test"
      }/packages/${channel}/package_${input._config?.frame?.model}.json.zip`,
      prices: `${baseURL}/extra/image/rxcbe/${
        env() || "test"
      }/prices/${channel}/prices_${country}.json`,
    };

    const contentPromise = input._content
      ? Promise.resolve(input._content)
      : getLensesDataContent(URLS.content).then((contentResponse) =>
          Array.isArray(contentResponse) && contentResponse.length > 0
            ? contentResponse[0]?.content || {}
            : {},
        );

    const packagesPromise = input._packages
      ? Promise.resolve(input._packages)
      : getLensesDataPackages(
          URLS.packages,
          input._config?.frame?.model as string,
          input._config?.frame?.color as string,
        );

    let [content, packages] = await Promise.all([
      contentPromise,
      packagesPromise,
    ]);

    if (packages && packages.length > 0) {
      if (noPricesInPackages(packages)) {
        const pricesPromise = input._prices
          ? Promise.resolve(input._prices)
          : getLensesDataPrices(URLS.prices);
        const prices = await pricesPromise;
        if (input._prices) {
          console.log("prices from lensesData.prices");
          packages = mergeLensesDataPrices(packages, prices);
        } else {
          console.log("prices from akamai");
          packages = mergeAkamaiPrices(packages, prices);
        }
      }
    }

    console.log("suggestions content-packages", content, packages);
    //prepareResponse
    if (content && packages) {
      let selected = packages?.find(
        (p) => p.lensPackage.upc == input._currentPackage,
      );
      if (!selected) {
        selected = packages?.find(
          (p) => p.lensPackage.catEntryId == input._currentPackage,
        );
      }
      const filteredPackages = packages?.filter((pkg) => {
        // Filter by valid prescription
        if (!hasValidPrescription(pkg)) return false;

        // Filter out packages with lower prices than selected
        const selectedOfferPrice = selected?.lensPackage?.offerPrice || 0;
        const selectedListPrice = selected?.lensPackage?.listPrice || 0;
        const pkgOfferPrice = pkg?.lensPackage?.offerPrice || 0;
        const pkgListPrice = pkg?.lensPackage?.listPrice || 0;

        // Keep packages that have higher or equal offer price AND list price
        return (
          parseFloat(pkgOfferPrice) >= parseFloat(selectedOfferPrice) &&
          parseFloat(pkgListPrice) >= parseFloat(selectedListPrice)
        );
      });
      if (selected) {
        //getProposals
        const upsellSuggestions = [
          getFirstSuggestion(selected.lensPackage, filteredPackages),
          getSecondSuggestion(selected.lensPackage, filteredPackages),
          getThirdSuggestion(selected.lensPackage, filteredPackages),
          getFourthSuggestion(selected.lensPackage, filteredPackages),
        ];

        console.log("suggestions", upsellSuggestions);

        const upsellPromises = upsellSuggestions.map((lens, index) =>
          lens
            ? prepareResponseMapper(
                lens,
                {
                  _content: content,
                  _packages: packages,
                  _currentPackage: input._currentPackage,
                  _locale: input._locale,
                  _imagery: input._imagery,
                  _config: input._config,
                },
                UPSELL_ORDERED_KEY_LIST[index],
              )
            : null,
        );

        const upsellSuggestionsResponse = await Promise.all(upsellPromises);

        console.log("suggestions mapped", upsellSuggestionsResponse);

        return upsellSuggestionsResponse;
      }
    }
  }
  return [];
}
